import { z } from "zod";

import {
  VIBE_LOG_GATEWAY_DEFAULT_LIMIT,
  VIBE_LOG_GATEWAY_MAX_CONTAINS_LENGTH,
  VIBE_LOG_GATEWAY_MAX_LIMIT,
  VIBE_LOG_GATEWAY_MAX_RANGE_MS,
  VIBE_LOG_PIPELINE_REASONS,
  VIBE_LOG_PIPELINE_STATUSES,
  VibeLogColorSchema,
  VibeLogPageSchema,
  VibeLogPipelineHealthSchema
} from "../../../../schemas/VibeTenant";
import { enumReadSchema } from "../../../../shared/core/enum-read.schema";

/**
 * The control-plane read of an app's RUNTIME logs — the first surface a Nexus
 * user can call for what a deployed app has printed.
 *
 * ## Every bound here is a SECOND enforcement, never the only one
 *
 * `vibe-log-gateway` re-validates all of it on the tenant's own router node and
 * clamps what it is handed. Both layers, deliberately: this one exists so a bad
 * request fails before it leaves the control plane, and the gateway's exists
 * because the gateway must be safe against a caller that is not this backend.
 * Neither is redundant with the other, and neither may be deleted on the
 * grounds that the other exists.
 *
 * The ceilings are IMPORTED from `log-gateway-endpoints.ts` rather than
 * restated. A number typed twice is a number that drifts, and drifting in the
 * generous direction here means the control plane accepts a window the gateway
 * then refuses — which surfaces to a user as "logs are broken" rather than as
 * "ask for less".
 *
 * ## 🔴 NO FIELD NAMES A HOST, AND NONE EVER MAY
 *
 * The gateway host is derived from the caller's ORGANIZATION, inside the use
 * case, after the org assertion. There is deliberately no `host`, `gateway`,
 * `tenant`, `cluster` or `url` field on this contract, and both schemas below
 * are `.strict()` so one cannot be smuggled in as an unknown key either.
 *
 * That is what makes a cross-tenant read STRUCTURALLY impossible rather than
 * merely refused: there is no input a caller can supply that changes which
 * tenant is addressed. A validated host field would reintroduce the hole — the
 * validation would be the only thing standing between a caller and another
 * org's log store, and a validator is a thing that can have a bug.
 *
 * `app-logs-contract-carries-no-host.test.ts` asserts this by enumerating the
 * schema's own keys, so a field added later fails the suite rather than
 * quietly widening the boundary.
 */

export const GetVibeAppLogsPathVarsSchema = z
  .object({
    /**
     * The app whose logs to read. Resolved ORG-SCOPED: an app in another
     * organization is a miss, indistinguishable from an id that never existed.
     */
    appId: z.string().uuid()
  })
  .strict();

export type GetVibeAppLogsPathVars = z.infer<typeof GetVibeAppLogsPathVarsSchema>;

/**
 * What a caller may ask about a window of logs.
 *
 * Every value arrives as a STRING on a query string, so the numeric fields
 * coerce. `z.coerce.number()` on `""` yields `0` rather than a failure, which is
 * why `from`/`to` are `.int()` and the window is additionally `refine`d — an
 * empty `to` would otherwise coerce to 0 and read as "the epoch", i.e. an
 * inverted window rather than a bad request.
 *
 * `contains` is the only free text and it never becomes query syntax: the
 * gateway composes the LogQL itself and passes this as an escaped literal
 * operand. There is no field through which a caller could supply a regular
 * expression, a label name, a selector or an aggregation, here or downstream.
 */
export const GetVibeAppLogsQuerySchema = z
  .object({
    /** Window start, inclusive, as epoch milliseconds. */
    from: z.coerce.number().int().nonnegative(),
    /** Window end, exclusive, as epoch milliseconds. */
    to: z.coerce.number().int().nonnegative(),
    /** Restrict to one deployment slot. Absent means both. */
    color: VibeLogColorSchema.optional(),
    /** Case-sensitive LITERAL substring. Never a regular expression. */
    contains: z.string().trim().min(1).max(VIBE_LOG_GATEWAY_MAX_CONTAINS_LENGTH).optional(),
    /** Lines per page, newest first. */
    limit: z.coerce
      .number()
      .int()
      .min(1)
      .max(VIBE_LOG_GATEWAY_MAX_LIMIT)
      .default(VIBE_LOG_GATEWAY_DEFAULT_LIMIT),
    /**
     * Page further back from here — the previous page's `nextCursor`, a
     * NANOSECOND epoch timestamp as a string. Nanoseconds because that is
     * Loki's own entry resolution: a millisecond cursor cannot separate two
     * lines emitted inside the same millisecond, so paging on one either
     * repeats or drops them.
     *
     * A string rather than a number on purpose — a nanosecond epoch exceeds
     * `Number.MAX_SAFE_INTEGER`, so coercing it would silently round the
     * cursor and move the page boundary.
     */
    cursor: z
      .string()
      .regex(/^[0-9]+$/, "cursor must be a nanosecond epoch timestamp")
      .optional()
  })
  .strict()
  .refine((value) => value.from < value.to, {
    message: "`from` must be strictly before `to`"
  })
  .refine((value) => value.to - value.from <= VIBE_LOG_GATEWAY_MAX_RANGE_MS, {
    message: "the queried window must not exceed 7 days"
  });

export type GetVibeAppLogsQuery = z.infer<typeof GetVibeAppLogsQuerySchema>;

/**
 * One page of log lines, newest first, plus the cursor for the page before it.
 *
 * Aliased from the gateway's own wire contract rather than re-declared: the
 * control plane relays what the gateway rendered, so a second declaration would
 * be a second definition of one payload, free to drift from the thing actually
 * producing it.
 */
/**
 * The log page as the console READS it: the pipeline's `status` and `reason` read
 * leniently, so a newer backend's verdict reaches an older console as an unlisted
 * value instead of failing the log view. The gateway's own page schema stays
 * strict for the gateway that writes it.
 */
export const GetVibeAppLogsResponseSchema = VibeLogPageSchema.extend({
  pipeline: VibeLogPipelineHealthSchema.extend({
    status: enumReadSchema(VIBE_LOG_PIPELINE_STATUSES),
    reason: enumReadSchema(VIBE_LOG_PIPELINE_REASONS)
  }).optional()
});

export type GetVibeAppLogsResponseDto = z.infer<typeof GetVibeAppLogsResponseSchema>;
